import { auth } from '../config/firebase';

/**
 * API関連のユーティリティ関数
 */

/**
 * 環境に応じてAPIベースURLを取得
 */
export const getApiBaseUrl = (): string => {
  // 環境変数からAPIベースURLを取得
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

  if (!apiBaseUrl) {
    console.warn('VITE_API_BASE_URL環境変数が設定されていません');
    // フォールバック: 開発環境のデフォルト
    return 'http://localhost:8000';
  }

  return apiBaseUrl;
};

/**
 * フルAPIエンドポイントURLを生成
 * @param endpoint エンドポイントパス（/api/から始まる）
 * @returns フルURL
 */
export const getApiUrl = (endpoint: string): string => {
  const baseUrl = getApiBaseUrl();

  // エンドポイントが/api/で始まらない場合は追加
  const cleanEndpoint = endpoint.startsWith('/api/')
    ? endpoint
    : `/api/${endpoint.replace(/^\/+/, '')}`;

  // ベースURLの末尾のスラッシュを除去してから結合
  return `${baseUrl.replace(/\/+$/, '')}${cleanEndpoint}`;
};

/**
 * APIリクエスト用のfetchラッパー
 * @param endpoint エンドポイントパス
 * @param options fetchオプション
 * @returns Response
 */
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> => {
  const url = getApiUrl(endpoint);

  // デフォルトヘッダーを設定
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Firebase ID Tokenを取得して設定
  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken();
      headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.warn('Firebase ID Tokenの取得に失敗しました:', error);
    }
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  };

  console.log(`🌐 API Request: ${config.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      console.error(`❌ API Error: ${response.status} ${response.statusText} - ${url}`);
    }

    return response;
  } catch (error) {
    console.error(`❌ Network Error: ${url}`, error);
    throw error;
  }
};

/**
 * APIレスポンスのJSONパース用ヘルパー
 * @param response Response オブジェクト
 * @returns JSONパース結果またはnull
 */
export const parseApiResponse = async <T = any>(response: Response): Promise<T | null> => {
  try {
    if (!response.ok) {
      return null;
    }

    const contentType = response.headers.get('Content-Type');
    if (!contentType?.includes('application/json')) {
      console.warn('レスポンスがJSON形式ではありません');
      return null;
    }

    return (await response.json()) as T;
  } catch (error) {
    console.error('JSONパースエラー:', error);
    return null;
  }
};
